import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import StepForm from '../../../../../features/components/Forms/StepForm'
import LineItemStep from './Steps/LineItemStep'
import ImpressionsStep from './Steps/ImpressionsStep'
import BudgetStep from './Steps/BudgetStep'
import DatesStep from './Steps/DatesStep'
import CustomAudiencesStep from './Steps/CustomAudiencesStep'
import GeolocationsStep from '../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/Steps/GeolocationsStep'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import useRightBudgetAndImpressionsCalculation from '../../../../Multiplatform/UploadAdCreative/useRightBudgetAndImpressionsCalculation'
import useLineItemCreateShowSteps from '../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/useLineItemCreateShowSteps'
import useLineItemCreateHandlers from './useLineItemCreateHandlers'
import useInitialDataManagement from './useInitialDataManagement'
import { getCampaignObjectiveBasedStep } from './Steps/campaignObjectiveBasedSteps/getCampaignObjectiveBasedStep'
import { getBillingEvent } from './formatters'

import { setRightUploadAdData } from '../../../../../modules/actions/rights'
import { clearLineItems } from '../../../../../modules/actions/lineItems'
import { rightSelector, tiktokRightUploadAdDataSelector } from '../../../../../modules/selectors/rights'
import { selectedTikTokAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import {
  lineItemCreatedDataSelector,
  lineItemCreateErrorSelector,
  lineItemCreateIsLoadingSelector,
  lineItemsSelector,
  lineItemWasCreatedSelector
} from '../../../../../modules/selectors/lineItems'
import { tikTokInterestsSelector, tikTokRegionsSelector } from '../../../../../modules/selectors/tikTokConstants'

import {
  DIGITAL_ALLOCATION_TYPE_BUDGET,
  DIGITAL_ALLOCATION_TYPE_IMPRESSIONS
} from '../../../../Multiplatform/Partnerships/RightForms/fields'
import { TIKTOK_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { LINE_ITEM_FORM } from '../../index'
import { REACH } from '../../../../../constants/campaigns'

import { getInitialValues, getValidationSchema, OPTIMIZATION_GOAL } from './fields.js'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'

const LineItemForm = ({ formProgressItems, onSuccessSubmit }) => {
  const dispatch = useDispatch()

  const [isFirstLineItemCreation, setIsFirstLineItemCreation] = useState(false)
  const [successSubmit, setSuccessSubmit] = useState(false)

  // line items are loaded when submitting previous CampaignForm with existing campaign option
  const lineItems = useSelector(lineItemsSelector)
  const lineItemCreatedData = useSelector(lineItemCreatedDataSelector)
  const lineItemWasCreated = useSelector(lineItemWasCreatedSelector)
  const right = useSelector(rightSelector)
  const { currency_symbol: currencySymbol } = useSelector(selectedTikTokAdAccountSelector)
  const { campaign } = useSelector(tiktokRightUploadAdDataSelector)
  const tikTokRegions = useSelector(tikTokRegionsSelector)
  const interests = useSelector(tikTokInterestsSelector)

  const { objective: campaignObjective, budget_optimize_on: isCampaignLevelBudgetSet } = campaign
  const isCampaignObjectiveReach = campaignObjective.toLowerCase() === REACH

  const { allocation_type: allocationTypeOnDigitalRight } = right

  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()

  const isLineItemsExist = !!lineItems.length

  const {
    // bid type
    isBidTypeCustomPopulated,
    isBidTypeNoBidPopulated,
    // pixel
    pixelIdFromExistingLineItem,
    externalActionFromExistingLineItem
  } = useLineItemCreateShowSteps({
    isCampaignLevelBudgetSet,
    campaignObjective
  })

  const shouldPopulateExistingValues = isCampaignLevelBudgetSet && !!lineItems.length
  const optimizationGoalFromExistingLineItem = shouldPopulateExistingValues ? lineItems[0][OPTIMIZATION_GOAL] : null

  const showImpressions =
    allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS && !isCampaignLevelBudgetSet

  const { onSubmit, clearCreateLineItemHandler } = useLineItemCreateHandlers({
    lineItems,
    right,
    campaign,
    tikTokRegions,
    setSuccessSubmit,
    shouldPopulateExistingValues,
    pixelIdFromExistingLineItem,
    externalActionFromExistingLineItem,
    optimizationGoalFromExistingLineItem,
    interests
  })

  const formik = useFormik({
    initialValues: getInitialValues({
      isLineItemsExist,
      campaignObjective,
      shouldPopulateExistingValues,
      optimizationGoalFromExistingLineItem
    }),
    validationSchema: getValidationSchema({
      isBidTypeCustomPopulated,
      isCampaignLevelBudgetSet,
      showImpressions,
      allocationTypeOnDigitalRight,
      remainingBudget,
      remainingImpressions,
      currencySymbol,
      isCampaignObjectiveReach,
      campaignObjective,
      shouldPopulateExistingValues
    }),
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values } = purifiedFormik

  const showLineItemStep = isLineItemsExist && !isFirstLineItemCreation

  const showOnlyLineItemStep = values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM

  const showBudgetStep = !showOnlyLineItemStep && !isCampaignLevelBudgetSet && !isCampaignLevelBudgetSet

  const showBidField = useMemo(() => {
    const billingEvent = getBillingEvent(campaignObjective, values[OPTIMIZATION_GOAL])

    if (isCampaignLevelBudgetSet && billingEvent === 'OCPM') {
      // if CBO is on & billing_event: OCPM then there is no bid to make -
      // it automatically chooses maximum delivery strategy instead
      return false
    }

    return (
      (allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_BUDGET && !isBidTypeNoBidPopulated) ||
      // if the right has impressions allocation - for reach campaigns we don't ask bid, but just use bid from the right
      (allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS && !isCampaignObjectiveReach)
    )
  }, [
    allocationTypeOnDigitalRight,
    isBidTypeNoBidPopulated,
    isCampaignObjectiveReach,
    isCampaignLevelBudgetSet,
    campaignObjective,
    values
  ])

  const showDatesStep = !showOnlyLineItemStep

  const showCustomAudiencesStep = !showOnlyLineItemStep
  const showGeolocationsStep = !showOnlyLineItemStep

  const steps = useMemo(
    () => [
      {
        component: <LineItemStep isOnlyLineItemStep={showOnlyLineItemStep} />,
        show: showLineItemStep
      },
      {
        component: showImpressions ? (
          <ImpressionsStep remainingImpressions={remainingImpressions} />
        ) : (
          <BudgetStep remainingBudget={remainingBudget} />
        ),
        show: showBudgetStep
      },
      {
        component: <DatesStep />,
        show: showDatesStep
      },
      {
        component: <GeolocationsStep />,
        show: showGeolocationsStep
      },
      getCampaignObjectiveBasedStep({
        campaignObjective,
        showBidField,
        shouldPopulateExistingValues,
        isBidTypeNoBidPopulated,
        showOnlyLineItemStep
      }),

      {
        component: <CustomAudiencesStep />,
        show: showCustomAudiencesStep
      }
    ],
    [
      remainingBudget,
      remainingImpressions,
      showBudgetStep,
      showCustomAudiencesStep,
      showDatesStep,
      showImpressions,
      showLineItemStep,
      showOnlyLineItemStep,
      campaignObjective,
      showBidField,
      shouldPopulateExistingValues,
      isBidTypeNoBidPopulated,
      showGeolocationsStep
    ]
  )

  useInitialDataManagement({
    isLineItemsExist,
    setIsFirstLineItemCreation
  })

  useEffect(() => {
    if (lineItemWasCreated) {
      dispatch(setRightUploadAdData({ lineItem: lineItemCreatedData }, TIKTOK_PLATFORM))

      setSuccessSubmit(true)
    }
  }, [dispatch, lineItemWasCreated, lineItemCreatedData])

  useEffect(
    () => () => {
      dispatch(clearLineItems())
    },
    [dispatch]
  )

  return (
    <StepForm
      steps={steps}
      formik={purifiedFormik}
      formName={LINE_ITEM_FORM}
      formProgressItems={formProgressItems}
      submitText="Save and continue"
      onSuccessSubmit={onSuccessSubmit}
      clearSubmitHandler={clearCreateLineItemHandler}
      successSubmit={successSubmit}
      errorSelector={lineItemCreateErrorSelector}
      isLoadingSelector={lineItemCreateIsLoadingSelector}
    />
  )
}

export default LineItemForm
